import { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, User, Mail, Phone, Award, TrendingUp, TrendingDown, Calendar, MinusCircle } from "react-feather";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge
} from "reactstrap";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import { baseURL } from "../../../../Services/api/baseURL";
import Loader from "../../../Loader/Loader";

const LoyaltyHistoryPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const navigationData = location.state;

    const fetchUserHistory = async () => {
        console.log("UserId from params:", userId);

        if (!userId || userId === 'undefined') {
            toast.error("Invalid user ID!");
            navigate('/loyalty');
            return;
        }

        setIsLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("token"));
            const response = await axios.get(
                `${baseURL}/api/admin/loyalty/history/${userId}`,
                {
                    headers: {
                        Authorization: `${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                const data = response.data.data;
                setUserData({
                    user: data.user,
                    balance: data.balance,
                    totalEarned: data.totalEarned,
                    totalRedeemed: data.totalRedeemed,
                    totalDeducted: data.totalDeducted
                });
                setHistoryData(data.pointsHistory || []);
            }
        } catch (error) {
            console.error("API Error:", error);
            console.error("Full error response:", error.response);

            if (error.response?.status === 404) {
                toast.error("User loyalty history not found!");
            } else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Failed to fetch user history!");
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserHistory();
    }, [userId]);

    // Navigation handlers
    const handleBack = () => {
        navigate('/loyalty');
    };

    const handlePrevNext = (direction) => {
        if (!navigationData) return;

        const { currentIndex, totalUsers, filters } = navigationData;
        let newIndex;

        if (direction === 'prev') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : totalUsers - 1;
        } else {
            newIndex = currentIndex < totalUsers - 1 ? currentIndex + 1 : 0;
        }

        toast.info(`Navigation to ${direction} user - Implementation needed based on your data structure`);
    };

    const historyColumns = [
        {
            name: 'Date',
            selector: row => row.date,
            sortable: true,
            width: '200px',
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <Calendar size={14} className="me-2 text-muted" />
                    <span>{moment(row.date).format('DD MMM YYYY')}</span>
                </div>
            )
        },
        {
            name: 'Type',
            selector: row => row.type,
            sortable: true,
            width: '200px',
            cell: (row) => {
                let color = 'secondary';
                let label = row.type;
                let icon = null;

                if (row.type === 'earn') {
                    color = 'success';
                    label = 'Earned';
                    icon = <TrendingUp size={12} className="me-1" />;
                } else if (row.type === 'redeem') {
                    color = 'danger';
                    label = 'Redeemed';
                    icon = <TrendingDown size={12} className="me-1" />;
                } else if (row.type === 'deduct') {
                    color = 'warning';
                    label = 'Deducted';
                    icon = <MinusCircle size={12} className="me-1" />;
                }

                return (
                    <Badge
                        color={color}
                        className="d-flex align-items-center"
                        style={{ width: 'fit-content' }}
                    >
                        {icon} {label}
                    </Badge>
                );
            }
        },
        {
            name: 'Points',
            selector: row => row.points,
            sortable: true,
            width: '200px',
            cell: (row) => (
                <span className={`fw-bold ${row.type === 'earn' ? 'text-success' : 'text-danger'}`}>
                    {row.type === 'earn' ? '+' : '-'}{row.points}
                </span>
            )
        },
{
  name: <div style={{ textAlign: "left", width: "100%", paddingLeft: "12px" }}>Reason</div>,
  cell: (row) => (
    <div style={{ textAlign: "left", width: "100%", paddingLeft: "12px" }}>
      {row.reason}
    </div>
  ),
  grow: 3,
}



    ];

    if (isLoading) {
        return (
            <Container fluid style={{ paddingTop: '30px' }}>
                <Loader />
            </Container>
        );
    }

    if (!userData) {
        return (
            <Container fluid style={{ paddingTop: '30px' }}>
                <Card>
                    <CardBody className="text-center py-5">
                        <h5>User not found</h5>
                        <Button color="primary" onClick={handleBack}>
                            <ArrowLeft size={16} className="me-2" />
                            Back to Loyalty Program
                        </Button>
                    </CardBody>
                </Card>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container fluid style={{ paddingTop: '30px' }}>
                {/* Header with Navigation */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <Button
                                    color="light"
                                    size="sm"
                                    onClick={handleBack}
                                    className="me-3"
                                >
                                    <ArrowLeft size={16} />
                                </Button>
                                <h4 className="mb-0">Loyalty History - {userData.user.name}</h4>
                            </div>

                            {navigationData && (
                                <div className="d-flex gap-2">
                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        onClick={() => handlePrevNext('prev')}
                                    >
                                        ← Previous User
                                    </Button>
                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        onClick={() => handlePrevNext('next')}
                                    >
                                        Next User →
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* User Info Cards */}
                <Row className="mb-2">
                    <Col lg={4} md={6} className="mb-3">
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="avatar-wrapper me-3">
                                        <div
                                            className="avatar bg-light-primary d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px', borderRadius: '12px' }}
                                        >
                                            <User size={24} className="text-primary" />
                                        </div>
                                    </div>
                                    <div>
                                        <h6 className="mb-0">Customer Info</h6>
                                        <div className="text-muted small">
                                            <div className="d-flex align-items-center mb-1">
                                                <Mail size={12} className="me-2" />
                                                {userData.user.email || 'N/A'}
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <Phone size={12} className="me-2" />
                                                {userData.user.phone || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={2} md={6} className="mb-3">
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="avatar-wrapper me-3">
                                        <div
                                            className="avatar bg-light-success d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px', borderRadius: '12px' }}
                                        >
                                            <Award size={24} className="text-success" />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="mb-0 text-success">{userData.balance}</h4>
                                        <span className="text-muted">Current Balance</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={2} md={6} className="mb-3">
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="avatar-wrapper me-3">
                                        <div
                                            className="avatar bg-light-info d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px', borderRadius: '12px' }}
                                        >
                                            <TrendingUp size={24} className="text-info" />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="mb-0 text-info">{userData.totalEarned}</h4>
                                        <span className="text-muted">Total Earned</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={2} md={6} className="mb-3">
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="avatar-wrapper me-3">
                                        <div
                                            className="avatar bg-light-danger d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px', borderRadius: '12px' }}
                                        >
                                            <TrendingDown size={24} className="text-danger" />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="mb-0 text-danger">{userData.totalRedeemed}</h4>
                                        <span className="text-muted">Total Redeemed</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={2} md={6} className="mb-3">
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="avatar-wrapper me-3">
                                        <div
                                            className="avatar bg-light-warning d-flex align-items-center justify-content-center"
                                            style={{ width: '50px', height: '50px', borderRadius: '12px' }}
                                        >
                                            <MinusCircle size={24} className="text-warning" />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="mb-0 text-warning">{userData.totalDeducted}</h4>
                                        <span className="text-muted">Total Deducted</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Summary Stats */}
                {historyData.length > 0 && (
                    <Row className="mt-1">
                        <Col>
                            <Card>
                                <CardBody>
                                    <h6 className="mb-3">Quick Stats</h6>
                                    <Row>
                                        <Col md={3}>
                                            <div className="text-center">
                                                <h5 className="text-success">{historyData.filter(h => h.type === 'earn').length}</h5>
                                                <span className="text-muted small">Earn Transactions</span>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center">
                                                <h5 className="text-danger">{historyData.filter(h => h.type === 'redeem').length}</h5>
                                                <span className="text-muted small">Redeem Transactions</span>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center">
                                                <h5 className="text-info">
                                                    {historyData.length > 0 ?
                                                        Math.round(userData.totalEarned / historyData.filter(h => h.type === 'earn').length || 0)
                                                        : 0
                                                    }
                                                </h5>
                                                <span className="text-muted small">Avg Points per Earn</span>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-center">
                                                <h5 className="text-primary">
                                                    {moment(historyData[0]?.date).format('DD MMM YYYY')}
                                                </h5>
                                                <span className="text-muted small">Last Activity</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* History Table */}
                <Row>
                    <Col>
                        <Card>
                            <CardBody style={{ padding: '15px' }}>
                                <Row xxl={12} className='pb-2'>
                                    <Row>
                                        <Col md={6} lg={6} xl={6} xxl={6}>
                                            <div>
                                                <h5 className='mb-0 font-bold'>
                                                    Points History ({historyData.length} transactions)
                                                </h5>
                                            </div>
                                        </Col>
                                        {/* <Col md={6} lg={6} xl={6} xxl={6}>
                                            <div className="file-content file-content1 justify-content-end">
                                                <Button 
                                                    color="primary" 
                                                    size="sm"
                                                    onClick={() => {
                                                        // Export functionality
                                                        toast.info("Export functionality coming soon!");
                                                    }}
                                                >
                                                    <DollarSign size={14} className="me-2" />
                                                    Export History
                                                </Button>
                                            </div>
                                        </Col> */}
                                    </Row>
                                </Row>
                            </CardBody>

                            {historyData.length > 0 ? (
                                <DataTable
                                    data={historyData}
                                    columns={historyColumns}
                                    striped={true}
                                    center={true}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                    noDataComponent={
                                        <div className="text-center py-4">
                                            <Award size={48} className="text-muted mb-3" />
                                            <h6>No loyalty history found</h6>
                                            <p className="text-muted">This user hasn't earned or redeemed any points yet.</p>
                                        </div>
                                    }
                                />
                            ) : (
                                <div className="text-center py-5">
                                    <Award size={48} className="text-muted mb-3" />
                                    <h6>No loyalty history found</h6>
                                    <p className="text-muted">This user hasn't earned or redeemed any points yet.</p>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default LoyaltyHistoryPage;